import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { of, Subscription } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService, AuthStatus, AuthToken } from 'src/app/api/auth.service';
import { ProfileService } from 'src/app/api/profile.service';
import { TierOneVerificationService } from 'src/app/api/tier-one-verification.service';
import { SegmentUserInfo, SegmentTracks, SegmentEvents } from 'src/app/models/segment-analytics';
import { RoutePages } from 'src/app/models/route-pages';
import { LocaleService } from './locale.service';

declare global {
  interface Window { analytics: any; }
}

@Injectable({
  providedIn: 'root'
})
export class SegmentAnalyticsService {
  private _token: AuthToken;
  private _sessionId: string;

  loginChangesSubscription: Subscription;
  routeChangesSubscription: Subscription;

  constructor(
    private auth: AuthService,
    private router: Router,
    private profileService: ProfileService,
    private tierOneVerificationService: TierOneVerificationService,
    private localeService: LocaleService,
  ) { }

  init() {
    return;
    const scriptKey = environment.segmentAnalytics.key;

    if (!scriptKey) {
      return;
    }

    // init
    SegmentAnalyticsService._loadScript(scriptKey);

    // Auth status changes
    this.loginChangesSubscription?.unsubscribe();
    this.loginChangesSubscription = this.auth.statusChanges.subscribe(status => {
      if (status === AuthStatus.LoggedIn) {
        this._token = this.auth.getToken();
        this._sessionId = this.auth.getSessionId();

        this.identifyUser();
        this._trackLogin();
      } else if (status === AuthStatus.LoggedOff) {
        this._token = null;
        this._sessionId = null;
        this.reset();
      }
    });

    this._token = this.auth.getToken();
    this._sessionId = this.auth.getSessionId();


    // Route Changes
    this.routeChangesSubscription?.unsubscribe();
    this.routeChangesSubscription = this.router.events.pipe(
      filter(e => e instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.sendPage(event.urlAfterRedirects);
    });
  }

  SendTrack(event: SegmentEvents, data: any) {
    // window.analytics?.track(event, data);
  }

  sendPage(page: RoutePages | string) {
    // window.analytics?.page(page);
  }

  identify(id: string, data: any) {
    if (id) {
      // window.analytics?.identify(id, data);
    } else {
      // window.analytics?.identify(data);
    }
  }

  reset() {
    // window.analytics?.reset();
  }

  async identifyUser() {
    const userInfo = await this._getUserInfo();

    this.identify(this._token?.userId, {
      ...userInfo,
      ...this.getCommonInfo()
    });
  }

  private async _trackLogin() {
    this.SendTrack(SegmentTracks.Login, {
      ...this.getCommonInfo()
    });
  }

  private getCommonInfo() {
    return {
      language: this.localeService.currentLocale,
      sourcePlatform: environment.api.type,
      sessionId: this._sessionId
    };
  }

  private _getUserInfo(): Promise<SegmentUserInfo> {
    return this.profileService.get().pipe(
      map(profile => ({ email: profile.email } as SegmentUserInfo)),
      catchError(() => of(null))
    ).toPromise();
  }

  private static _loadScript(scriptKey: string) {
    try {
      const scriptElement = document.createElement('script');
      scriptElement.async = true;
      scriptElement.type = 'text/javascript';
      scriptElement.innerHTML = `!function(){var analytics=window.analytics=window.analytics||[];if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Segment snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware"];analytics.factory=function(e){return function(){var t=Array.prototype.slice.call(arguments);t.unshift(e);analytics.push(t);return analytics}};for(var e=0;e<analytics.methods.length;e++){var key=analytics.methods[e];analytics[key]=analytics.factory(key)}analytics.load=function(key,e){var t=document.createElement("script");t.type="text/javascript";t.async=!0;t.src="https://cdn.segment.com/analytics.js/v1/" + key + "/analytics.min.js";var n=document.getElementsByTagName("script")[0];n.parentNode.insertBefore(t,n);analytics._loadOptions=e};analytics._writeKey="${scriptKey}";;analytics.SNIPPET_VERSION="4.15.3";
        analytics.load("${scriptKey}");
        analytics.page();
      }}();`;
      document.head.appendChild(scriptElement);
    } catch (e) {
      console.error('Error appending segment analytics script', e);
    }
  }
}
